import { centsToFormattedDollars, formatDateTime } from '@fruitful/fruitils';
import {
    getDefaultBillingFrequency,
    MembershipBillingFrequency,
    type StripeSubscriptionSummary,
    SUBSCRIPTION_PRODUCT_BILLING_FREQUENCIES,
    SubscriptionChangeMoney,
    type SubscriptionChangePreview,
    type SubscriptionChangeRequest,
    SubscriptionChangeTiming,
    type SubscriptionPhaseSummary,
    SubscriptionProductId,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { capitalize } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledMultilineInput,
    ControlledPicker,
    FootnoteEmphasized,
    FootnoteRegular,
    Form,
    H5Regular,
    ScrollView,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { type MembershipDetails } from '../../entities/entityTypes';
import { useChangeSubscriptionAndRefetch } from '../hooks/useChangeSubscription';

/** Local labels for the picker; prefer product.displayName when a product is on the response. */
const SUBSCRIPTION_PRODUCT_LABELS: Record<SubscriptionProductId, string> = {
    [SubscriptionProductId.TRIAL_PREMIUM]: 'Premium Trial',
    [SubscriptionProductId.PREMIUM]: 'Premium',
    [SubscriptionProductId.STANDARD]: 'Fruitful',
    [SubscriptionProductId.PLUS]: 'Plus',
    [SubscriptionProductId.ESSENTIAL]: 'Essential',
    [SubscriptionProductId.JOINT]: 'Joint',
    [SubscriptionProductId.SOLO]: 'Solo',
};

/** Pre-commercial catalog. Not destinations we expect Grove to pick going forward. */
const LEGACY_SUBSCRIPTION_PRODUCT_IDS: ReadonlySet<SubscriptionProductId> =
    new Set([
        SubscriptionProductId.PLUS,
        SubscriptionProductId.ESSENTIAL,
        SubscriptionProductId.JOINT,
        SubscriptionProductId.SOLO,
    ]);

const isLegacySubscriptionProduct = (
    productId: SubscriptionProductId | undefined,
): boolean =>
    productId != null && LEGACY_SUBSCRIPTION_PRODUCT_IDS.has(productId);

const subscriptionProductPickerLabel = (
    productId: SubscriptionProductId,
): string => {
    const label = SUBSCRIPTION_PRODUCT_LABELS[productId];
    return isLegacySubscriptionProduct(productId) ? `${label} (legacy)` : label;
};

/**
 * Mirrors backend PLAN_CHANGE_RANK / default timing+money so Grove can label
 * "Policy default" before preview. Preview remains the authority.
 */
/** Mirrors backend billing.hasAnnualCommitment; only premium commits for a year. */
const ANNUAL_COMMITMENT_PRODUCT_IDS: ReadonlySet<SubscriptionProductId> =
    new Set([SubscriptionProductId.PREMIUM]);

/** `commitment_end` is rejected unless the source product commits annually or is the signup intro. */
const allowsCommitmentEndTiming = (
    fromProductId: SubscriptionProductId | undefined,
): boolean =>
    fromProductId != null &&
    (ANNUAL_COMMITMENT_PRODUCT_IDS.has(fromProductId) ||
        fromProductId === SubscriptionProductId.TRIAL_PREMIUM);

const PLAN_CHANGE_RANK: Partial<Record<SubscriptionProductId, number>> = {
    [SubscriptionProductId.STANDARD]: 0,
    [SubscriptionProductId.ESSENTIAL]: 1,
    [SubscriptionProductId.PREMIUM]: 2,
    [SubscriptionProductId.PLUS]: 3,
    [SubscriptionProductId.SOLO]: 3,
    [SubscriptionProductId.JOINT]: 3,
};

const estimatePolicyDefault = ({
    fromProductId,
    toProductId,
    fromBillingFrequency,
    toBillingFrequency,
    isOnIntroPhase,
}: {
    fromProductId: SubscriptionProductId | undefined;
    toProductId: SubscriptionProductId;
    fromBillingFrequency: MembershipBillingFrequency | undefined;
    toBillingFrequency: MembershipBillingFrequency;
    isOnIntroPhase: boolean;
}): {
    timing: SubscriptionChangeTiming;
    money: SubscriptionChangeMoney;
} | null => {
    if (fromProductId == null) {
        return null;
    }

    if (
        isOnIntroPhase ||
        fromProductId === SubscriptionProductId.TRIAL_PREMIUM
    ) {
        // Trial → Standard is the next phase signup already scheduled, so it
        // waits until the intro phase ends. Premium, Essential, Plus, Solo,
        // and Joint start now. The trial price is $0, so the credit is $0.
        if (toProductId === SubscriptionProductId.STANDARD) {
            return {
                timing: SubscriptionChangeTiming.PERIOD_END,
                money: SubscriptionChangeMoney.NONE,
            };
        }
        return {
            timing: SubscriptionChangeTiming.IMMEDIATE,
            money: SubscriptionChangeMoney.CREATE_PRORATIONS,
        };
    }

    if (
        fromProductId === toProductId &&
        fromBillingFrequency === toBillingFrequency
    ) {
        return null;
    }

    const fromRank = PLAN_CHANGE_RANK[fromProductId];
    const toRank = PLAN_CHANGE_RANK[toProductId];
    // Solo yearly is $998 and Premium is $999 or $1,000, so rank would call
    // this a downgrade. Match the backend carve-out and treat it as an upgrade.
    const soloYearlyToPremium =
        toProductId === SubscriptionProductId.PREMIUM &&
        fromProductId === SubscriptionProductId.SOLO &&
        fromBillingFrequency === MembershipBillingFrequency.YEARLY;
    const isUpgrade =
        soloYearlyToPremium ||
        (fromProductId !== toProductId &&
            fromRank != null &&
            toRank != null &&
            toRank > fromRank);

    if (isUpgrade) {
        return {
            timing: SubscriptionChangeTiming.IMMEDIATE,
            money: SubscriptionChangeMoney.CREATE_PRORATIONS,
        };
    }

    if (fromProductId === SubscriptionProductId.PREMIUM) {
        return {
            timing: SubscriptionChangeTiming.COMMITMENT_END,
            money: SubscriptionChangeMoney.NONE,
        };
    }

    return {
        timing: SubscriptionChangeTiming.PERIOD_END,
        money: SubscriptionChangeMoney.NONE,
    };
};

const POLICY_DEFAULT = 'policy_default';

const TIMING_LABELS: Record<SubscriptionChangeTiming, string> = {
    [SubscriptionChangeTiming.COMMITMENT_END]: 'At commitment end',
    [SubscriptionChangeTiming.PERIOD_END]: 'At current period end',
    [SubscriptionChangeTiming.IMMEDIATE]: 'Immediate',
};

const MONEY_LABELS: Record<SubscriptionChangeMoney, string> = {
    [SubscriptionChangeMoney.NONE]: 'No invoice movement',
    [SubscriptionChangeMoney.CREATE_PRORATIONS]: 'Create prorations',
    [SubscriptionChangeMoney.REFUND_TO_SOURCE]:
        'Refund unused time to original payment method',
};

const changeSubscriptionSchema = z.object({
    subscriptionProductId: z.nativeEnum(SubscriptionProductId),
    billingFrequency: z.nativeEnum(MembershipBillingFrequency),
    timing: z.union([
        z.literal(POLICY_DEFAULT),
        z.nativeEnum(SubscriptionChangeTiming),
    ]),
    money: z.union([
        z.literal(POLICY_DEFAULT),
        z.nativeEnum(SubscriptionChangeMoney),
    ]),
    comment: z.string().max(500),
});

type ChangeSubscriptionSchema = z.infer<typeof changeSubscriptionSchema>;

interface ChangeSubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    membershipDetails: MembershipDetails;
}

const SUBSCRIPTION_PRODUCT_OPTIONS = Object.values(SubscriptionProductId)
    .filter(
        (subscriptionProductId) =>
            subscriptionProductId !== SubscriptionProductId.TRIAL_PREMIUM,
    )
    .map((subscriptionProductId) => ({
        label: subscriptionProductPickerLabel(subscriptionProductId),
        value: subscriptionProductId,
    }));

const hasPendingCancellation = (membershipDetails: MembershipDetails) =>
    membershipDetails.cancellationScheduledJobArn != null ||
    membershipDetails.users.some(
        (user) =>
            user.cancellationDate != null && user.isSubscriptionActive === true,
    );

const getSubscriptionDefaults = (
    subscription: StripeSubscriptionSummary | null | undefined,
): ChangeSubscriptionSchema => {
    const subscriptionProductId =
        subscription?.type == null ||
        subscription.type === SubscriptionProductId.TRIAL_PREMIUM
            ? SubscriptionProductId.PREMIUM
            : subscription.type;
    return {
        subscriptionProductId,
        billingFrequency:
            subscription?.type === SubscriptionProductId.TRIAL_PREMIUM
                ? getDefaultBillingFrequency(subscriptionProductId)
                : (subscription?.billingFrequency ??
                  getDefaultBillingFrequency(subscriptionProductId)),
        timing: POLICY_DEFAULT,
        money: POLICY_DEFAULT,
        comment: '',
    };
};

const getAvailableFrequencies = (
    subscriptionProductId: SubscriptionProductId,
): readonly MembershipBillingFrequency[] =>
    SUBSCRIPTION_PRODUCT_BILLING_FREQUENCIES[subscriptionProductId];

const paymentMethodLabel = ({
    hasPaymentMethod,
    hasDefaultPaymentMethod,
}: SubscriptionChangePreview['stripePreview']): string => {
    if (!hasPaymentMethod) {
        return 'Not on file';
    }

    return hasDefaultPaymentMethod ? 'On file' : 'Attached, not default';
};

const SectionHeading = ({ children }: { children: string }) => (
    <FootnoteEmphasized color="$gray1000" textTransform="uppercase">
        {children}
    </FootnoteEmphasized>
);

const DetailRow = ({
    label,
    value,
    isEmphasized = false,
}: {
    label: string;
    value: string;
    isEmphasized?: boolean;
}) => (
    <XStack
        justifyContent="space-between"
        gap={16}
        paddingVertical={6}
        borderBottomWidth={1}
        borderBottomColor="$gray200"
    >
        <BodySmallRegular color="$gray1000">{label}</BodySmallRegular>
        {isEmphasized ? (
            <BodySmallEmphasized textAlign="right">{value}</BodySmallEmphasized>
        ) : (
            <BodySmallRegular textAlign="right">{value}</BodySmallRegular>
        )}
    </XStack>
);

const currentPlanLabel = (subscription: StripeSubscriptionSummary): string => {
    const displayName = subscription.product?.displayName;
    const type = subscription.type;
    const base =
        displayName ??
        (type != null ? SUBSCRIPTION_PRODUCT_LABELS[type] : 'Unknown');
    if (isLegacySubscriptionProduct(type)) {
        return `${base} (legacy)`;
    }

    return base;
};

const phasePlanLabel = (phase: SubscriptionPhaseSummary): string => {
    if (phase.product?.displayName != null) {
        return phase.product.displayName;
    }

    return phase.subscriptionProductId != null
        ? SUBSCRIPTION_PRODUCT_LABELS[phase.subscriptionProductId]
        : phase.priceId;
};

/** Pending schedule phase: what the member moves to when the current plan ends. */
const ScheduledNextPhase = ({ phase }: { phase: SubscriptionPhaseSummary }) => (
    <YStack gap={12}>
        <SectionHeading>Scheduled next</SectionHeading>
        <YStack>
            <DetailRow
                label="Plan"
                value={phasePlanLabel(phase)}
                isEmphasized={true}
            />
            {phase.billingFrequency != null && (
                <DetailRow
                    label="Billing frequency"
                    value={capitalize(phase.billingFrequency)}
                />
            )}
            {phase.billingAmountCents != null && (
                <DetailRow
                    label="Amount"
                    value={centsToFormattedDollars(phase.billingAmountCents)}
                />
            )}
            <DetailRow
                label="Starts"
                value={formatDateTime(phase.startDate)}
                isEmphasized={true}
            />
            {phase.endDate != null && (
                <DetailRow label="Ends" value={formatDateTime(phase.endDate)} />
            )}
        </YStack>
    </YStack>
);

const buildChangeRequest = ({
    subscriptionProductId,
    billingFrequency,
    timing,
    money,
    comment,
}: ChangeSubscriptionSchema): SubscriptionChangeRequest => ({
    subscriptionProductId,
    billingFrequency,
    ...(timing !== POLICY_DEFAULT ? { timing } : {}),
    ...(money !== POLICY_DEFAULT ? { money } : {}),
    ...(comment.trim() !== '' ? { comment: comment.trim() } : {}),
});

/* eslint-disable complexity -- The two-step form coordinates conditional policy controls and preview states. */
export const ChangeSubscriptionModal = ({
    isOpen,
    onClose,
    membershipDetails,
}: ChangeSubscriptionModalProps) => {
    const { id, subscription } = membershipDetails;
    const toast = useToastController();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [preview, setPreview] = useState<SubscriptionChangePreview | null>(
        null,
    );
    const { previewSubscriptionChangeAsync, applySubscriptionChangeAsync } =
        useChangeSubscriptionAndRefetch(membershipDetails);

    const defaults = getSubscriptionDefaults(subscription);

    const {
        control,
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors, isValid },
    } = useForm<ChangeSubscriptionSchema>({
        resolver: zodResolver(changeSubscriptionSchema),
        defaultValues: defaults,
    });

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        reset(getSubscriptionDefaults(subscription));
        setPreview(null);
    }, [isOpen, reset, subscription]);

    const selectedSubscriptionProductId = watch('subscriptionProductId');
    const selectedBillingFrequency = watch('billingFrequency');
    const selectedTiming = watch('timing');
    const selectedMoney = watch('money');
    const comment = watch('comment');
    const availableFrequencies = getAvailableFrequencies(
        selectedSubscriptionProductId,
    );

    useEffect(() => {
        if (availableFrequencies.includes(selectedBillingFrequency)) {
            return;
        }

        const nextFrequency = availableFrequencies[0];
        if (nextFrequency == null) {
            return;
        }

        setValue('billingFrequency', nextFrequency, { shouldValidate: true });
    }, [availableFrequencies, selectedBillingFrequency, setValue]);

    const billingFrequencyOptions = useMemo(
        () =>
            availableFrequencies.map((frequency) => ({
                label: capitalize(frequency),
                value: frequency,
            })),
        [availableFrequencies],
    );

    const pendingCancellation = hasPendingCancellation(membershipDetails);
    const hasSubscription = subscription != null;
    const isUnchanged =
        hasSubscription &&
        subscription.type !== SubscriptionProductId.TRIAL_PREMIUM &&
        selectedSubscriptionProductId === subscription.type &&
        selectedBillingFrequency === subscription.billingFrequency;
    const estimatedPolicyDefault = estimatePolicyDefault({
        fromProductId: subscription?.type,
        toProductId: selectedSubscriptionProductId,
        fromBillingFrequency: subscription?.billingFrequency,
        toBillingFrequency: selectedBillingFrequency,
        isOnIntroPhase:
            subscription?.type === SubscriptionProductId.TRIAL_PREMIUM,
    });
    const canUseCommitmentEnd = allowsCommitmentEndTiming(subscription?.type);
    const timingOptions = useMemo(
        () => [
            {
                label:
                    estimatedPolicyDefault == null
                        ? 'Policy default'
                        : `Policy default (${TIMING_LABELS[estimatedPolicyDefault.timing]})`,
                value: POLICY_DEFAULT,
            },
            ...Object.values(SubscriptionChangeTiming)
                .filter(
                    (timing) =>
                        timing !== SubscriptionChangeTiming.COMMITMENT_END ||
                        canUseCommitmentEnd,
                )
                .map((timing) => ({
                    label: TIMING_LABELS[timing],
                    value: timing,
                })),
        ],
        [canUseCommitmentEnd, estimatedPolicyDefault],
    );
    const effectiveTiming =
        selectedTiming === POLICY_DEFAULT
            ? (preview?.policy.timing ?? estimatedPolicyDefault?.timing)
            : selectedTiming;
    const moneyOptions = useMemo(
        () => [
            {
                label:
                    estimatedPolicyDefault == null
                        ? 'Policy default'
                        : `Policy default (${MONEY_LABELS[estimatedPolicyDefault.money]})`,
                value: POLICY_DEFAULT,
            },
            ...Object.values(SubscriptionChangeMoney)
                .filter(
                    (money) =>
                        effectiveTiming !==
                            SubscriptionChangeTiming.IMMEDIATE ||
                        money !== SubscriptionChangeMoney.NONE,
                )
                .map((money) => ({
                    label: MONEY_LABELS[money],
                    value: money,
                })),
        ],
        [effectiveTiming, estimatedPolicyDefault],
    );
    const isFormLocked =
        !hasSubscription || pendingCancellation || isSubmitting;
    const selectingLegacyDestination = isLegacySubscriptionProduct(
        selectedSubscriptionProductId,
    );
    // Money only moves on an immediate change. Immediate cannot be "no invoice
    // movement"; that combination used to swap the price and leave the period.
    const isMoneyLocked =
        effectiveTiming != null &&
        effectiveTiming !== SubscriptionChangeTiming.IMMEDIATE;
    const missingPaymentMethod =
        preview?.stripePreview.missingPaymentMethodForMoneyMovement === true;
    const canPreview = isValid && !isFormLocked && !isUnchanged;
    const canApply = canPreview && preview != null && !missingPaymentMethod;

    useEffect(() => {
        setPreview(null);
    }, [
        comment,
        selectedBillingFrequency,
        selectedSubscriptionProductId,
        selectedMoney,
        selectedTiming,
    ]);

    useEffect(() => {
        if (isMoneyLocked && selectedMoney !== POLICY_DEFAULT) {
            setValue('money', POLICY_DEFAULT, { shouldValidate: true });
        }
    }, [isMoneyLocked, selectedMoney, setValue]);

    useEffect(() => {
        if (
            effectiveTiming === SubscriptionChangeTiming.IMMEDIATE &&
            selectedMoney === SubscriptionChangeMoney.NONE
        ) {
            setValue('money', POLICY_DEFAULT, { shouldValidate: true });
        }
    }, [effectiveTiming, selectedMoney, setValue]);

    useEffect(() => {
        if (
            selectedTiming === SubscriptionChangeTiming.COMMITMENT_END &&
            !canUseCommitmentEnd
        ) {
            setValue('timing', POLICY_DEFAULT, { shouldValidate: true });
        }
    }, [canUseCommitmentEnd, selectedTiming, setValue]);

    const previewChange: SubmitHandler<ChangeSubscriptionSchema> = async (
        values,
    ) => {
        setIsSubmitting(true);

        try {
            setPreview(
                await previewSubscriptionChangeAsync(
                    id,
                    buildChangeRequest(values),
                ),
            );
        } catch (error) {
            toast.show(
                'Error previewing subscription change',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const applyChange: SubmitHandler<ChangeSubscriptionSchema> = async (
        _values,
    ) => {
        if (preview == null || missingPaymentMethod) {
            return;
        }

        setIsSubmitting(true);

        try {
            await applySubscriptionChangeAsync(id, preview.applyPayload);
            toast.show(
                'Subscription change applied',
                MutationSuccessToastOptions,
            );
            onClose();
        } catch (error) {
            toast.show(
                'Error changing subscription',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitPreview = () => {
        void handleSubmit(previewChange)();
    };

    const submitSubscriptionChange = () => {
        if (!canApply) {
            return;
        }

        void handleSubmit(applyChange)();
    };

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            hasCloseButton={true}
            onClose={onClose}
            renderInAppRoot={false}
        >
            <ScrollView maxHeight="80vh">
                <YStack
                    marginHorizontal={16}
                    marginTop={32}
                    width={880}
                    gap={24}
                    marginBottom={24}
                >
                    <YStack gap={4}>
                        <H5Regular>Change subscription</H5Regular>
                        <FootnoteRegular color="$gray1000">
                            Preview the backend policy default before applying.
                            A delayed change keeps the current plan active until
                            its switch date.
                        </FootnoteRegular>
                    </YStack>
                    {!hasSubscription && (
                        <BodySmallRegular color="$red1000">
                            This membership has no Stripe subscription, so the
                            subscription cannot be changed here.
                        </BodySmallRegular>
                    )}
                    {pendingCancellation && (
                        <BodySmallRegular color="$red1000">
                            Stop the pending cancellation before changing the
                            subscription.
                        </BodySmallRegular>
                    )}
                    <Form onSubmit={submitSubscriptionChange}>
                        <YStack gap={24}>
                            <XStack gap={24} alignItems="flex-start">
                                <YStack
                                    flex={1}
                                    flexBasis={0}
                                    minWidth={0}
                                    gap={12}
                                    padding={16}
                                    borderWidth={1}
                                    borderColor="$gray300"
                                    borderRadius={8}
                                >
                                    <SectionHeading>Current</SectionHeading>
                                    {subscription == null ? (
                                        <BodySmallRegular color="$gray1000">
                                            No Stripe subscription
                                        </BodySmallRegular>
                                    ) : (
                                        <YStack>
                                            <DetailRow
                                                label="Plan"
                                                value={currentPlanLabel(
                                                    subscription,
                                                )}
                                                isEmphasized={true}
                                            />
                                            <DetailRow
                                                label="Billing frequency"
                                                value={capitalize(
                                                    subscription.billingFrequency,
                                                )}
                                            />
                                            <DetailRow
                                                label="Renews"
                                                value={formatDateTime(
                                                    subscription.renewalDate,
                                                )}
                                            />
                                            <DetailRow
                                                label="Status"
                                                value={capitalize(
                                                    subscription.status,
                                                )}
                                            />
                                            {subscription.cancelAtDate !=
                                                null && (
                                                <DetailRow
                                                    label="Cancels"
                                                    value={formatDateTime(
                                                        subscription.cancelAtDate,
                                                    )}
                                                />
                                            )}
                                        </YStack>
                                    )}
                                    {subscription?.nextPhase != null && (
                                        <ScheduledNextPhase
                                            phase={subscription.nextPhase}
                                        />
                                    )}
                                </YStack>
                                <YStack
                                    flex={1}
                                    flexBasis={0}
                                    minWidth={0}
                                    gap={12}
                                    padding={16}
                                    borderWidth={1}
                                    borderColor="$gray300"
                                    borderRadius={8}
                                >
                                    <SectionHeading>Target</SectionHeading>
                                    <ControlledPicker
                                        label="Subscription"
                                        name="subscriptionProductId"
                                        size="tiny"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        options={SUBSCRIPTION_PRODUCT_OPTIONS}
                                        disabled={isFormLocked}
                                    />
                                    {selectingLegacyDestination && (
                                        <BodySmallRegular color="$red1000">
                                            Legacy plans are not expected
                                            destinations. Prefer Fruitful or
                                            Premium unless you are correcting a
                                            current legacy member.
                                        </BodySmallRegular>
                                    )}
                                    <ControlledPicker
                                        label="Billing frequency"
                                        name="billingFrequency"
                                        size="tiny"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        options={billingFrequencyOptions}
                                        disabled={isFormLocked}
                                    />
                                    <DetailRow
                                        label="Switches"
                                        value={
                                            preview == null
                                                ? 'Preview to see'
                                                : preview.switchAt == null
                                                  ? 'Now'
                                                  : formatDateTime(
                                                        preview.switchAt,
                                                    )
                                        }
                                        isEmphasized={preview != null}
                                    />
                                </YStack>
                            </XStack>
                            <YStack gap={12}>
                                <SectionHeading>Overrides</SectionHeading>
                                <ControlledPicker
                                    label="Timing"
                                    name="timing"
                                    size="tiny"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    options={timingOptions}
                                    disabled={isFormLocked || isUnchanged}
                                />
                                <ControlledPicker
                                    label={
                                        isMoneyLocked
                                            ? 'Money (immediate changes only)'
                                            : 'Money'
                                    }
                                    name="money"
                                    size="tiny"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    options={moneyOptions}
                                    disabled={
                                        isFormLocked ||
                                        isUnchanged ||
                                        isMoneyLocked
                                    }
                                />
                                <ControlledMultilineInput
                                    name="comment"
                                    label={
                                        preview?.commentRequiredHint === true
                                            ? 'Comment (recommended for override)'
                                            : 'Comment (optional)'
                                    }
                                    size="tiny"
                                    placeholder="Why this change?"
                                    fontWeight="regular"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    disabled={isFormLocked || isUnchanged}
                                />
                            </YStack>
                            {preview != null && (
                                <YStack
                                    gap={16}
                                    padding={16}
                                    borderWidth={1}
                                    borderColor="$gray300"
                                    borderRadius={8}
                                >
                                    <BodySmallEmphasized>
                                        Preview
                                    </BodySmallEmphasized>
                                    <YStack gap={4}>
                                        <SectionHeading>Policy</SectionHeading>
                                        <YStack>
                                            <DetailRow
                                                label="Direction"
                                                value={capitalize(
                                                    preview.policy.direction.replace(
                                                        /_/g,
                                                        ' ',
                                                    ),
                                                )}
                                            />
                                            <DetailRow
                                                label="Timing"
                                                value={
                                                    TIMING_LABELS[
                                                        preview.policy.timing
                                                    ]
                                                }
                                            />
                                            <DetailRow
                                                label="Money"
                                                value={
                                                    MONEY_LABELS[
                                                        preview.policy.money
                                                    ]
                                                }
                                            />
                                            <DetailRow
                                                label="Source"
                                                value={
                                                    preview.policy.isDefault
                                                        ? 'Policy default'
                                                        : 'Grove override'
                                                }
                                                isEmphasized={
                                                    !preview.policy.isDefault
                                                }
                                            />
                                        </YStack>
                                    </YStack>
                                    <YStack gap={4}>
                                        <SectionHeading>
                                            Schedule
                                        </SectionHeading>
                                        <YStack>
                                            <DetailRow
                                                label="Switches"
                                                value={
                                                    preview.switchAt == null
                                                        ? 'Now'
                                                        : formatDateTime(
                                                              preview.switchAt,
                                                          )
                                                }
                                                isEmphasized={true}
                                            />
                                            <DetailRow
                                                label="Current period ends"
                                                value={formatDateTime(
                                                    preview.currentPeriodEnd,
                                                )}
                                            />
                                            {preview.commitmentEndsAt !=
                                                null && (
                                                <DetailRow
                                                    label="Commitment ends"
                                                    value={formatDateTime(
                                                        preview.commitmentEndsAt,
                                                    )}
                                                />
                                            )}
                                        </YStack>
                                    </YStack>
                                    <YStack gap={4}>
                                        <SectionHeading>Money</SectionHeading>
                                        <YStack>
                                            <DetailRow
                                                label="Due now"
                                                value={centsToFormattedDollars(
                                                    preview.stripePreview
                                                        .amountDueNowCents,
                                                )}
                                                isEmphasized={true}
                                            />
                                            <DetailRow
                                                label="Estimated refund"
                                                value={centsToFormattedDollars(
                                                    preview.stripePreview
                                                        .estimatedRefundCents,
                                                )}
                                            />
                                            <DetailRow
                                                label="Payment method"
                                                value={paymentMethodLabel(
                                                    preview.stripePreview,
                                                )}
                                                isEmphasized={
                                                    missingPaymentMethod ||
                                                    !preview.stripePreview
                                                        .hasDefaultPaymentMethod
                                                }
                                            />
                                        </YStack>
                                    </YStack>
                                    {preview.stripePreview.lineSummaries
                                        .length > 0 && (
                                        <YStack gap={4}>
                                            <SectionHeading>
                                                Invoice lines
                                            </SectionHeading>
                                            <YStack>
                                                {preview.stripePreview.lineSummaries.map(
                                                    (line, index) => (
                                                        <DetailRow
                                                            key={`${
                                                                line.description ??
                                                                'line'
                                                            }-${index}`}
                                                            label={
                                                                line.description ??
                                                                'Invoice line'
                                                            }
                                                            value={centsToFormattedDollars(
                                                                line.amountCents,
                                                            )}
                                                        />
                                                    ),
                                                )}
                                            </YStack>
                                        </YStack>
                                    )}
                                    {preview.warnings.length > 0 && (
                                        <YStack gap={4}>
                                            <SectionHeading>
                                                Warnings
                                            </SectionHeading>
                                            {preview.warnings.map((warning) => (
                                                <FootnoteRegular
                                                    key={warning}
                                                    color="$red1000"
                                                >
                                                    {warning}
                                                </FootnoteRegular>
                                            ))}
                                        </YStack>
                                    )}
                                </YStack>
                            )}
                            <XStack gap={12}>
                                <View>
                                    <Button
                                        disabled={!canPreview}
                                        variant={ButtonVariant.SECONDARY}
                                        size={ButtonSize.TINY}
                                        text="Preview change"
                                        onPress={submitPreview}
                                    />
                                </View>
                                <View>
                                    <Button
                                        disabled={!canApply}
                                        variant={ButtonVariant.PRIMARY}
                                        size={ButtonSize.TINY}
                                        text="Apply subscription change"
                                        onPress={submitSubscriptionChange}
                                    />
                                </View>
                            </XStack>
                        </YStack>
                    </Form>
                </YStack>
            </ScrollView>
        </GroveSimpleModal>
    );
};
/* eslint-enable complexity */
